import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import Breadcrumbs from '@/components/Breadcrumbs'
import SubmittalStatusPill from '@/components/SubmittalStatusPill'
import Button from '@/components/Button'
import Icon from '@/components/Icon/Icon'
import ChevronLeft from '@/assets/svgs/chevron-left.svg?react'
import { formatDate, formatDateTime } from '@/utils/formatDate'
import { SUBMITTAL_STATUS } from '../constants'
import { LISTING_PATH } from '../listing-config'

export const SubmittalHeader = ({
  elementName,
  status,
  projectContractName,
  submissionMonth,
  submissionDate,
  modified,
  secondaryApproval,
  onDownload,
  isDownloading = false,
  backTo = LISTING_PATH,
  listingCrumb = null,
  compactDates = false,
}) => {
  const navigate = useNavigate()
  // Off, the month prints as the column stores it and the timestamp keeps its
  // padded hour — which is exactly what the EMS detail page renders in
  // production, and this page serves that too.
  //
  // On, both take pitstop's formats: "YYYY-MM" as "MM/YYYY", the hour without
  // its leading zero. The ISO form is listed in the parse formats because the
  // column is a timestamp for some elements, which would otherwise print whole
  // and read as a submission date rather than a month.
  const submissionMonthText =
    compactDates && submissionMonth
      ? moment(submissionMonth, ['YYYY-MM', moment.ISO_8601]).format('MM/YYYY')
      : submissionMonth
  // The second metadata item differs by element: EMS submits for a month, an
  // upload element submits on a date. One slot, two labels.
  //
  // The month is what decides, because the date cannot: the service stamps
  // `submission_date` on EVERY submittal at submit, so an EMS submittal carries
  // one too, and keying on its presence would show EMS a date instead of its
  // month. Only an element whose schema has no month field falls through to the
  // date. A draft has neither and shows no second item at all.
  //
  // One format either way, unlike the month above — `DD/MM/YYYY h:mma` is what
  // the configured listing prints for the same column, and the reader has just
  // come from that table.
  const submissionDateText = submissionDate ? formatDateTime(submissionDate) : ''
  const showSubmissionDate = !submissionMonthText && Boolean(submissionDateText)
  const submissionMetaLabel = showSubmissionDate ? 'Submission Date' : 'Submission Month'
  const submissionMetaValue = showSubmissionDate ? submissionDateText : submissionMonthText
  // isHomePage false, which draws the home icon as a grey-50 underlined link to
  // /submittals rather than the inert grey-10 icon both listings draw. It reads
  // as inconsistent, and it duplicates the "Submittals" crumb beside it, but
  // this header serves the EMS detail page too — so the difference stays until
  // it is fixed for every page at once rather than silently here.
  //
  // Home and "Submittals" both go to `backTo`, not the bare listing: `backTo` is
  // already the tab the reader came from (or the bare listing, with the flag off
  // or before the submittal has loaded), so this matches the "View Other
  // Submissions" button and the back chevron below rather than reopening
  // whichever data element happens to load first.
  const breadcrumbItems = [
    { label: '', link: backTo, isHomePage: false },
    { label: 'Submittals', link: backTo },
    // The data element this submittal is listed on, once it is known. The crumb
    // used to jump to the bare listing, which lands on whichever element opens
    // first — not the one the reader came from.
    ...(listingCrumb ? [{ label: listingCrumb.label, link: listingCrumb.link }] : []),
    { label: 'Details' },
  ]

  return (
    <>
      <Breadcrumbs breadcrumbItems={breadcrumbItems} />
      <div className="flex items-start gap-3">
        <button
          type="button"
          className="phone:inline-flex hidden shrink-0 items-center justify-center w-9 h-9 p-0 mt-0.5 border-0 rounded-full bg-grey-95 cursor-pointer transition-colors duration-150 ease-in-out hover:bg-grey-90 focus:bg-grey-90 active:bg-grey-90 hover:outline-none focus:outline-none active:outline-none [&_svg]:w-5 [&_svg]:h-5 [&_svg_path]:fill-grey-10"
          onClick={() => navigate(backTo)}
          aria-label="Back to submittals"
        >
          <ChevronLeft />
        </button>
        <div className="flex flex-col gap-2 min-w-0 flex-1">
          <div className="flex flex-row items-center gap-2">
            <div className="phone:type-header-3 type-header-4 font-[900] min-w-0 truncate">
              {elementName || ''}
            </div>
            <SubmittalStatusPill status={status} secondaryApproval={secondaryApproval} />

            {/* pitstop's header also carries Edit Form and Notify Approver Again.
                Both are the submitter's: a consultant cannot edit someone else's
                submission, and they are themselves the approver being notified. */}
            {onDownload && (
              // The shared Button's default solid variant — theme-50 filled,
              // white, hovering to theme-40 — as a pill, which is pitstop's
              // download button (and the Approve button below it).
              // px-5 for pitstop's 20px; the download glyph's own fill attribute
              // needs the `!` to give way, since Icon's fill-current does not
              // override it.
              <Button
                type="button"
                round
                data-testid="submittal-download-btn"
                addClass="ml-auto shrink-0 px-5! [&_svg_path]:fill-white!"
                prefix={<Icon name="download" style="outlined" size="small" />}
                onClick={onDownload}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading...' : 'Download'}
              </Button>
            )}
          </div>
          <div className="flex phone:flex-row flex-col phone:items-center phone:gap-2 gap-1 font-avenir-medium text-grey-50 type-meta-medium">
            {projectContractName && <span>Project Contract Name: {projectContractName}</span>}
            {projectContractName && submissionMetaValue && (
              <span className="font-avenir-heavy text-grey-50 type-meta-heavy phone:inline hidden">
                ·
              </span>
            )}
            {submissionMetaValue && (
              <span>
                {submissionMetaLabel}: {submissionMetaValue}
              </span>
            )}
          </div>
          {modified && (
            <div className="font-avenir-medium text-grey-50 type-meta-medium">
              Last updated on: {formatDate(modified, { padHour: !compactDates })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

SubmittalHeader.propTypes = {
  elementName: PropTypes.string,
  status: PropTypes.string,
  projectContractName: PropTypes.string,
  submissionMonth: PropTypes.string,
  // Shown in the month's place, as "Submission Date", when the submittal has no
  // month. Passed only behind SUBMITTALS_PHASE_2, as pitstop gates it.
  submissionDate: PropTypes.string,
  modified: PropTypes.string,
  secondaryApproval: PropTypes.shape({
    required: PropTypes.bool,
    label: PropTypes.string,
  }),
  onDownload: PropTypes.func,
  isDownloading: PropTypes.bool,
  // The listing URL to return to, active tab included.
  backTo: PropTypes.string,
  listingCrumb: PropTypes.shape({
    label: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
  }),
  // Renders the month as MM/YYYY and the timestamp with an unpadded hour, as
  // pitstop does. Off by default, which is what keeps EMS reading as it does in
  // production — the same reason `onDownload` is only passed for the ported
  // pages.
  compactDates: PropTypes.bool,
}
